import {
  Badge,
  Card,
  CardActionArea,
  CardMedia,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { Fade, IconButton, Icons, SkeletonText } from 'utoniq-ui'

import {
  CurrentUserFragment,
  EventDetailCollectionBoxFragment,
  EventDetailFragment,
  EventPreSaleBoxFragment,
  Maybe,
  PageInfo,
} from '@/clients/utoniq-core/schema'
import {
  BaseLayout,
  Header,
  HeaderImageArea,
  SideMenu,
  SideMenuProps,
} from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  PreSaleCard,
  PreSaleCardSkeleton,
  StoreCard,
  StoreCardSkeleton,
} from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { GpsClientInfo, GpsMapModal } from '@/components/organisms/gps'
import { styles, styleSets } from '@/components/styles'
import { config } from '@/config'
import { User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'
import { usePrivateAction } from '@/lib/privateAction'
import { LinkifyBox } from '@/lib/reactLinkify'
import { replaceParameters } from '@/lib/url'

export * from './fragments'

export type State = {
  currentUser: User | null
  loading: boolean
  tab: 0 | 1
  hasNextPage: boolean
  hasPresalesNextPage: boolean
  event: EventDetailFragment | null
  collections: {
    edges: {
      node: EventDetailCollectionBoxFragment
    }[]
  } | null
  user: CurrentUserFragment | null
  isInside: boolean
  userCartCount: number
  gpsClientInfo: Maybe<GpsClientInfo>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  preSale: {
    edges: { node: EventPreSaleBoxFragment }[]
    pageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>
  } | null
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onRequestPermission: () => Promise<void>
  onFetchPreSalesMore: () => Promise<void>
}

export const EventDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onRequestPermission,
  onFetchPreSalesMore,
}) => {
  const [openMap, setOpenMap] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<number>(state.tab)
  const { router, generatePath, routerPages } = useRouter<{ eventId: string }>()
  const { t } = useTranslation('eventDetail')

  const handleTabChange = useCallback(
    (value: number) => {
      setCurrentTab(value)
      // If you use router.replace, the page will re-render, causing it to scroll to the top.
      // https://stackoverflow.com/a/61596862
      window.history.replaceState(
        window.history.state, // keep current state
        '',
        generatePath(routerPages.ShokufesEventDetail, {
          eventId: router.query.eventId,
          tab: value === 0 ? 'presale' : 'store',
        })
      )
    },
    [generatePath, router, routerPages.ShokufesEventDetail]
  )

  return (
    <BaseLayout>
      <EventHeaderCell
        currentUser={state.currentUser}
        loading={state.loading}
        user={state.user}
        event={state.event}
        userCartCount={state.userCartCount}
        followArtists={state.followArtists}
        memberArtists={state.memberArtists}
        isArtist={state.isArtist}
      />
      <IntroductionCell
        event={state.event}
        onClickMap={() => {
          onRequestPermission()
          setOpenMap(true)
        }}
      />
      <EventBannerInfoCell
        loading={state.loading}
        user={state.user}
        event={state.event}
      />
      {state.preSale?.edges.length ? (
        <Tabs
          labels={[t('presaleTickets'), t('storeCollection')]}
          value={currentTab}
          disableAnimateTransitions
          onChange={handleTabChange}
        >
          <PreSalesCell
            loading={state.loading}
            preSales={state.preSale}
            hasNextPage={state.hasPresalesNextPage}
            onFetchMore={onFetchPreSalesMore}
          />
          <StoreCollectionCell
            hasPreSale
            loading={state.loading}
            collections={state.collections}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
          />
        </Tabs>
      ) : (
        <StoreCollectionCell
          hasPreSale={false}
          loading={state.loading}
          collections={state.collections}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
        />
      )}
      <GpsMapModal
        title={state.event?.location?.approximateAddress ?? ''}
        open={openMap}
        onClose={() => setOpenMap(false)}
        targetData={state.event?.location}
        currentData={state.gpsClientInfo?.currentLocation}
        isInside={state.isInside}
        hideAnnotation
      />
    </BaseLayout>
  )
}

type EventHeaderCellProps = {
  loading: State['loading']
  event: State['event']
  user: State['user']
  memberArtists: State['memberArtists']
  isArtist: State['isArtist']
  followArtists: State['followArtists']
  userCartCount: State['userCartCount']
  currentUser: State['currentUser']
}
const EventHeaderCell: Cell<EventHeaderCellProps> = ({
  loading,
  event,
  user,
  userCartCount,
  isArtist,
  followArtists,
  memberArtists,
  currentUser,
}) => {
  const { routerPages, router } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const { handleLoginModal } = usePrivateAction()
  const { analytics } = useAnalytics()
  const imageUri = event?.thumbnail?.mediumUri ?? event?.thumbnail?.uri ?? ''

  if (loading && !event) {
    return (
      <CellFrame sx={{ pb: 5 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={212}
          sx={{
            pt: styles.safeAreaTop,
            '& > *': { visibility: 'visible' },
          }}
        >
          <Header
            color="transparent"
            elevation={0}
            back={true}
            sx={{ backgroundColor: 'transparent' }}
            right={
              <Stack direction="row" spacing={3}>
                <Stack
                  sx={{
                    borderRadius: '50%',
                    width: 44,
                    height: 44,
                    bgcolor: 'grey.50',
                  }}
                />
                <Stack
                  sx={{
                    borderRadius: '50%',
                    width: 44,
                    height: 44,
                    bgcolor: 'grey.50',
                  }}
                />
              </Stack>
            }
          />
        </Skeleton>
      </CellFrame>
    )
  }
  return (
    <CellFrame sx={{ position: 'relative' }}>
      <Header
        elevation={0}
        position="absolute"
        sx={{
          pt: 0,
          backgroundColor: 'transparent',
          '.MuiButtonBase-root': {
            bgcolor: 'grey.50',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          },
        }}
        back
        defaultBackPage={routerPages.ShokufesEvents}
        right={
          <Stack direction="row" spacing={3}>
            <IconButton.Notice
              bgcolor="grey.50"
              size="medium"
              onClick={handleLoginModal(() =>
                router.push(routerPages.Notifications)
              )}
              color="text.secondary"
            />
            <Badge
              badgeContent={userCartCount}
              color="primary"
              sx={{
                '& > span': ({ typography }) => ({
                  top: 8,
                  minWidth: 16,
                  width: 16,
                  height: 16,
                  ...typography.overline,
                  color: 'white',
                }),
              }}
            >
              <IconButton.Store
                edge="end"
                bgcolor="grey.50"
                size="medium"
                onClick={handleLoginModal(() => {
                  analytics?.track('click_cart', {
                    applicationKey: config.applicationKey,
                    type: 'eventDetail',
                    isAnonymous: currentUser?.isAnonymous,
                  })
                  router.push(routerPages.StoreCartList)
                }, true)}
              />
            </Badge>
          </Stack>
        }
      />
      <HeaderImageArea
        hasSafeArea
        bgImage={imageUri}
        imageUri={imageUri}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
      />
      <SideMenu
        open={openSideMenu}
        onClose={() => setOpenSideMenu(false)}
        user={user}
        isArtist={isArtist}
        followArtists={followArtists}
        memberArtists={memberArtists}
      />
    </CellFrame>
  )
}

type IntroductionCellProps = {
  event: State['event']
  onClickMap: () => void
}
const IntroductionCell: Cell<IntroductionCellProps> = ({
  event,
  onClickMap,
}) => {
  const { t } = useTranslation('eventDetail')
  const { onClick: onClickMapLink } = useBrowserLink(
    event?.location?.mapUri ?? ''
  )
  const { analytics } = useAnalytics()
  if (!event) return null

  return (
    <CellFrame sx={{ px: 5, pb: 5 }}>
      <Typography variant="h3" sx={{ mt: 5 }}>
        {event.title}
      </Typography>
      <LinkifyBox>
        <Typography variant="body2" sx={{ mt: 4, wordBreak: 'break-all' }}>
          {event.description}
        </Typography>
      </LinkifyBox>
      {event.dateRange && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 4 }}>
            {t('startedAt', '開催日')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {event.dateRange}
          </Typography>
        </>
      )}
      {!!event.formattedEventTime && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 4 }}>
            {t('eventTime', '開催時間')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {event.formattedEventTime}
          </Typography>
        </>
      )}
      {event.location && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 4 }}>
            {t('address', '会場')}
          </Typography>
          <Stack
            sx={{ mt: 1, cursor: 'pointer' }}
            direction="row"
            alignItems="center"
          >
            <Stack
              direction="row"
              alignItems="center"
              minWidth={0}
              onClick={() => {
                analytics?.track('click_eventMap', {
                  applicationKey: config.applicationKey,
                  eventId: event?.id,
                  type: 'eventDetail',
                })

                onClickMap()
              }}
            >
              <Icons.Pin color="primary" />
              <Typography
                variant="subtitle1"
                color="primary"
                align="left"
                sx={{ ...styleSets.lineCamp(1) }}
              >
                {event.location?.approximateAddress}
              </Typography>
            </Stack>
            {!!event.location?.mapUri && (
              <Stack
                direction="row"
                onClick={() => {
                  analytics?.track('click_eventMapLink', {
                    applicationKey: config.applicationKey,
                    eventId: event?.id,
                    type: 'eventDetail',
                  })
                  onClickMapLink()
                }}
              >
                <Typography variant="subtitle1" display="inherit" minWidth={90}>
                  （
                  <Typography
                    component="span"
                    variant="subtitle1"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                  >
                    {t('venueMap', '会場MAP')}
                  </Typography>
                  ）
                </Typography>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </CellFrame>
  )
}

type EventBannerInfoCellProps = {
  loading: State['loading']
  user: State['user']
  event?: State['event']
}
const EventBannerInfoCell: Cell<EventBannerInfoCellProps> = ({
  loading,
  user,
  event,
}) => {
  if (loading) {
    return (
      <CellFrame sx={{ px: 5, pb: 5 }}>
        <Stack spacing={5}>
          <Skeleton variant="rectangular" width="100%" height={100} />
          <Skeleton variant="rectangular" width="100%" height={100} />
        </Stack>
      </CellFrame>
    )
  }

  if (!event?.banners?.length) return null

  return (
    <CellFrame sx={{ px: 5, pb: 5 }}>
      <Stack spacing={5}>
        {event?.banners?.map((banner, index) => (
          <Fade key={index}>
            <Card elevation={0} sx={{ border: 'none' }}>
              {banner.link?.externalUrl ? (
                <CardActionArea
                  component="a"
                  target="_blank"
                  href={
                    replaceParameters(banner.link?.externalUrl, {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    image={banner.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              ) : (
                <CardActionArea
                  href={
                    replaceParameters(banner.link?.innerPath ?? '', {
                      uid: user?.id ?? '',
                    }) || ''
                  }
                >
                  <CardMedia
                    component="img"
                    height={140}
                    image={banner.thumbnail.mediumUri || ''}
                  />
                </CardActionArea>
              )}
            </Card>
          </Fade>
        ))}
      </Stack>
    </CellFrame>
  )
}

type PreSalesCellProps = {
  preSales: State['preSale']
  loading: boolean
  hasNextPage: boolean
  onFetchMore: Action['onFetchPreSalesMore']
}

const PreSalesCell: Cell<PreSalesCellProps> = ({
  preSales,
  loading,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('eventDetail')
  return (
    <CellFrame sx={{ px: 5, py: 4 }}>
      <ScrollFetcher
        nodes={preSales?.edges.map(({ node }) => (
          <PreSaleCard preSale={node} key={node.id} />
        ))}
        componentProps={{ spacing: 5 }}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        emptyMessage={t('storeCollectionEmpty')}
        loadingComponent={
          <Stack spacing={3} sx={{ pt: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <PreSaleCardSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}

type StoreCollectionCellProps = {
  hasPreSale: boolean
  loading: boolean
  hasNextPage: boolean
  collections: State['collections']
  onFetchMore: Action['onFetchMore']
}
const StoreCollectionCell: Cell<StoreCollectionCellProps> = ({
  hasPreSale,
  loading,
  hasNextPage,
  collections,
  onFetchMore,
}) => {
  const { t } = useTranslation('eventDetail')
  return (
    <CellFrame sx={{ px: 5, py: 4 }}>
      {hasPreSale ? null : (
        <>
          {loading && !collections?.edges.length ? (
            <SkeletonText variant="large" width="50%" />
          ) : (
            <Typography variant="h3" sx={{ py: 2 }}>
              {t('storeCollection', '販売中のストア')}
            </Typography>
          )}
        </>
      )}
      <ScrollFetcher
        nodes={collections?.edges.map(({ node }) => (
          <StoreCard eventCollection={node} key={node.id} />
        ))}
        componentProps={{ spacing: 5 }}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        emptyMessage={t('storeCollectionEmpty')}
        loadingComponent={
          <Stack spacing={3} sx={{ pt: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <StoreCardSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}
